import { npubOf } from "@/utils/nip19"
import { toSingleLine } from "@/utils/format"
import type { NostrEvent, Profile } from "../types"

function safeString(value: unknown, max = 600): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  return trimmed.slice(0, max)
}

function safeUrl(value: unknown): string | undefined {
  const text = safeString(value, 600)
  if (!text) return undefined
  return /^https?:\/\//i.test(text) ? text : undefined
}

/** Parses a kind 0 metadata event into a defensive profile object. */
export function parseProfileEvent(event: NostrEvent | undefined): Profile | undefined {
  if (!event || event.kind !== 0) return undefined
  let raw: Record<string, unknown> = {}
  try {
    const parsed = JSON.parse(event.content) as unknown
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      raw = parsed as Record<string, unknown>
    }
  } catch {
    return { pubkey: event.pubkey, npub: npubOf(event.pubkey), createdAt: event.created_at }
  }

  const name = safeString(raw.name, 80) ?? safeString(raw.display_name, 80)
  const displayName = safeString(raw.display_name, 80) ?? name
  return {
    pubkey: event.pubkey,
    npub: npubOf(event.pubkey),
    name,
    displayName,
    about: safeString(raw.about, 2000),
    picture: safeUrl(raw.picture),
    banner: safeUrl(raw.banner),
    nip05: safeString(raw.nip05, 200),
    lud16: safeString(raw.lud16, 200),
    lud06: safeString(raw.lud06, 400),
    website: safeUrl(raw.website),
    createdAt: event.created_at,
  }
}

/** Fallback profile for a pubkey that has not published kind 0 yet. */
export function placeholderProfile(pubkey: string): Profile {
  return { pubkey, npub: npubOf(pubkey) }
}

export function profileLabel(profile: Profile | undefined, pubkey: string): string {
  if (!profile) return `${pubkey.slice(0, 8)}…${pubkey.slice(-4)}`
  return toSingleLine(profile.displayName || profile.name || `${pubkey.slice(0, 8)}…`, 60)
}

export function profileFilter(pubkeys: string[]): { authors: string[]; kinds: number[] } {
  return { authors: pubkeys, kinds: [0] }
}
